import { groupLabels } from './group-labels-config';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Warning from '@mui/icons-material/Warning';

interface GroupLabelsCheckboxesProps {
  checkedStates: { [key: string]: boolean };
  onStateChange: (name: string, checked: boolean) => void;
  disabledStates?: { [key: string]: boolean };
  isLoading?: boolean;
  accessGranted: boolean;
}

const LoadingSkeleton = () => (
  <Stack direction="column" spacing={2}>
    {groupLabels.map((label) => (
      <Stack
        key={label.key}
        direction="row"
        alignItems="flex-start"
        spacing={2}
      >
        <Skeleton
          variant="rectangular"
          width={24}
          height={24}
          sx={{ borderRadius: 1, mt: '6px' }}
        />
        <Box width={'100%'}>
          <Skeleton
            variant="text"
            height={30}
            sx={{ fontSize: '1rem' }}
            width={160}
          />
          <Skeleton
            variant="text"
            height={50}
            sx={{ fontSize: '1rem' }}
            width={'100%'}
          />
          {label.warning && (
            <Stack
              direction="row"
              alignItems="flex-start"
              spacing={1}
              sx={{ mt: 1 }}
            >
              <Skeleton variant="circular" width={20} height={20} />
              <Skeleton
                variant="text"
                sx={{ fontSize: '0.875rem' }}
                width="80%"
              />
            </Stack>
          )}
        </Box>
      </Stack>
    ))}
  </Stack>
);

export default function GroupLabelsCheckboxes({
  checkedStates,
  onStateChange,
  disabledStates = {},
  isLoading,
  accessGranted,
}: GroupLabelsCheckboxesProps) {
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  const mailingLabel = groupLabels.find((label) => label.name === 'isMailing')!;
  const securityLabel = groupLabels.find(
    (label) => label.name === 'isSecurity'
  )!;
  const lockedLabel = groupLabels.find((label) => label.name === 'isLocked')!;

  const renderCheckbox = (
    label: typeof mailingLabel | typeof securityLabel | typeof lockedLabel
  ) => (
    <Box key={label.key}>
      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={2}
        sx={{
          mb: 2,
          ml: accessGranted === false && label.name === 'isMailing' ? 6.15 : 0,
        }}
      >
        <Checkbox
          sx={{ p: 0 }}
          checked={checkedStates[label.name] || false}
          onChange={(e) => onStateChange(label.name, e.target.checked)}
          disabled={disabledStates[label.name] || false}
        />
        <Box>
          <Typography fontWeight={500}>{label.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {label.description}
            {label.readmoreLink && (
              <>
                {' '}
                <Link href={label.readmoreLink} target="_blank" rel="noopener">
                  {`Learn about ${label.readmoreName}`}
                </Link>
              </>
            )}
          </Typography>

          {label.warning && (
            <Stack
              direction="row"
              alignItems="flex-start"
              spacing={1}
              sx={{ mt: 1 }}
            >
              {checkedStates[label.name] && (
                <Warning fontSize="small" sx={{ color: 'warning.main' }} />
              )}
              <Typography
                variant="body2"
                color={
                  checkedStates[label.name] ? 'warning.main' : 'text.secondary'
                }
              >
                {label.warning}
              </Typography>
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );

  return (
    <>
      {renderCheckbox(mailingLabel)}
      {accessGranted === true && (
        <Stack spacing={0}>
          {renderCheckbox(securityLabel)}
          {renderCheckbox(lockedLabel)}
        </Stack>
      )}
      {accessGranted === false && (
        <Alert severity="warning">
          You have not{' '}
          <Link
            href="https://gpanel.io/knowledge/getting-started/enabling-new-scopes"
            target="_blank"
            rel="noopener noreferrer"
          >
            granted data access
          </Link>{' '}
          to the Cloud Identity API for gPanel.
          <Stack mt={2}>
            {renderCheckbox(securityLabel)}
            {renderCheckbox(lockedLabel)}
          </Stack>
        </Alert>
      )}
    </>
  );
}
